import api from './api';
import { User, UserRole, ModulePermission, PermissionsConfig } from '@/types';

export interface UserCreateByAdmin {
  email: string;
  full_name: string;
  role: UserRole;
}

export interface UserUpdate {
  email?: string;
  full_name?: string;
  role?: UserRole;
  is_active?: boolean;
}

export interface UserCreateResponse {
  user: User;
  temporary_password: string;
  email_sent: boolean;
}

export const usersService = {
  async getUsers(includeInactive = false): Promise<User[]> {
    const response = await api.get<User[]>('/users', {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },

  async getUser(id: number): Promise<User> {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  async createUser(data: UserCreateByAdmin): Promise<UserCreateResponse> {
    const response = await api.post<UserCreateResponse>('/users', data);
    return response.data;
  },

  async updateUser(id: number, data: UserUpdate): Promise<User> {
    const response = await api.put<User>(`/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: number): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async reactivateUser(id: number): Promise<User> {
    const response = await api.post<User>(`/users/${id}/reactivate`);
    return response.data;
  },

  async getPermissionsConfig(): Promise<PermissionsConfig> {
    const response = await api.get<PermissionsConfig>('/users/permissions/config');
    return response.data;
  },

  async getUserPermissions(id: number): Promise<ModulePermission[]> {
    const response = await api.get<ModulePermission[]>(`/users/${id}/permissions`);
    return response.data;
  },

  async updateUserPermissions(id: number, permissions: ModulePermission[]): Promise<User> {
    const response = await api.put<User>(`/users/${id}/permissions`, {
      module_permissions: permissions
    });
    return response.data;
  },

  async resetUserPermissions(id: number): Promise<User> {
    const response = await api.delete<User>(`/users/${id}/permissions`);
    return response.data;
  },
};

// Helper para obtener el nombre del rol en español
export const getRoleName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    superadmin: 'Super Admin',
    admin: 'Administrador',
    cashier: 'Cajero',
    employee: 'Empleado',
  };
  return roleNames[role] || role;
};

// Helper para obtener el color del badge del rol
export const getRoleColor = (role: UserRole): string => {
  const roleColors: Record<UserRole, string> = {
    superadmin: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-800',
    cashier: 'bg-amber-100 text-amber-800',
    employee: 'bg-gray-100 text-gray-800',
  };
  return roleColors[role] || 'bg-gray-100 text-gray-800';
};

// Roles disponibles para crear usuarios (sin superadmin).
// "Cajero" queda oculto en el front por ahora; los labels/colores se mantienen para
// mostrar correctamente usuarios existentes que ya tengan ese rol.
export const availableRoles: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Administrador' },
  { value: 'employee', label: 'Empleado' },
];
